import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components/native';

import { Feather } from '@expo/vector-icons';

import api from '../../../../api/api';
import ActivityIndicatorScreen from '../../../../components/ActivityIndicatorScreen';
import ListItem from '../../../../components/list/ListItem';
import ListItemAccessory from '../../../../components/list/ListItemAccessory';
import ListItemText from '../../../../components/list/ListItemText';
import PdfModal from '../../../../components/PdfModal';
import Spacer from '../../../../components/Spacer';
import { B } from '../../../../components/Typography';
import { useStudyContext } from '../../../../context/StudyContext';
import { useUserContext } from '../../../../context/UserContext';
import {
  StudyParticipantDownloadableFormDto,
  useGetStudyParticipantDownloadableForms
} from '../../../../hooks/study/useGetStudyParticipantDownloadableForms';
import {
  ScreenComponentPlugin,
  ScreenComponentProps
} from '../ScreenComponentPlugin';
import Card from '../../../../components/Card';
import { useFocusEffect } from '@react-navigation/native';

const StyledCardView = styled(Card)`
  background: #ffeaf4;
  width: 100%;
  padding: 18px 22px 18px 16px;
`;

const StyledListItem = styled(ListItem)`
  margin: 0px;
`;

interface DownloadableFormsCardProps {
  title?: string;
}

function hasFormDownloadTarget(
  item: StudyParticipantDownloadableFormDto
): item is StudyParticipantDownloadableFormDto & {
  formTemplateId: number;
  entityDataId: number;
} {
  return item.formTemplateId != null && item.entityDataId != null;
}

function hasDocumentDownloadTarget(
  item: StudyParticipantDownloadableFormDto
): item is StudyParticipantDownloadableFormDto & {
  documentId: number;
} {
  return item.documentId != null;
}

function getDownloadUrl(
  item: StudyParticipantDownloadableFormDto,
  studyParticipantId: number
): string | null {
  if (hasDocumentDownloadTarget(item)) {
    return `${api.getBaseUrl()}participant-documents/${item.documentId}/download`;
  }
  if (hasFormDownloadTarget(item)) {
    return `${api.getBaseUrl()}study-participants/${studyParticipantId}/downloadable-forms/${item.formTemplateId}/${item.entityDataId}`;
  }
  return null;
}

function getDownloadableItemKey(
  item: StudyParticipantDownloadableFormDto,
  index: number
): string {
  if (hasDocumentDownloadTarget(item)) {
    return `document-${item.documentId}`;
  }
  if (hasFormDownloadTarget(item)) {
    return `form-${item.formTemplateId}-${item.entityDataId}`;
  }
  return `item-${index}`;
}

const DownloadableFormsCard: React.FC<
  ScreenComponentProps<DownloadableFormsCardProps>
> = ({ title }) => {
  const { studyParticipantId } = useStudyContext();
  const { downloadableForms, loadingDownloadableForms, getDownloadableForms } =
    useGetStudyParticipantDownloadableForms(studyParticipantId || 0);

  useFocusEffect(
    useCallback(() => {
      if (studyParticipantId) getDownloadableForms();
    }, [studyParticipantId, getDownloadableForms])
  );

  const [selectedItem, setSelectedItem] =
    useState<StudyParticipantDownloadableFormDto>();

  const selectedDownloadUrl = useMemo(() => {
    if (!selectedItem || !studyParticipantId) {
      return null;
    }
    return getDownloadUrl(selectedItem, studyParticipantId);
  }, [selectedItem, studyParticipantId]);

  const renderItem = (
    item: StudyParticipantDownloadableFormDto,
    index: number
  ) => (
    <StyledListItem
      key={getDownloadableItemKey(item, index)}
      onPress={() => {
        if (
          studyParticipantId &&
          getDownloadUrl(item, studyParticipantId)
        ) {
          setSelectedItem(item);
        }
      }}
    >
      <ListItemText secondary={item.name} accessoryPadding={48} />
      <ListItemAccessory>
        <Feather name='chevron-right' size={24} />
      </ListItemAccessory>
    </StyledListItem>
  );

  return (
    <StyledCardView>
      <B color='dark'>{title || 'Download Documents'}</B>
      {loadingDownloadableForms && (
        <>
          <Spacer size={2} />
          <ActivityIndicatorScreen
            backgroundColor='transparent'
            color='white'
          />
          <Spacer size={4} />
        </>
      )}
      {(downloadableForms ?? []).map(renderItem)}
      {selectedItem && selectedDownloadUrl && studyParticipantId && (
        <DownloadableItemPdfModal
          item={selectedItem}
          downloadUrl={selectedDownloadUrl}
          onRequestClose={() => setSelectedItem(undefined)}
        />
      )}
    </StyledCardView>
  );
};

const DownloadableItemPdfModal: React.FC<{
  item: StudyParticipantDownloadableFormDto;
  downloadUrl: string;
  onRequestClose: () => void;
}> = ({ item, downloadUrl, onRequestClose }) => {
  const { accessToken } = useUserContext();

  const cacheKey = hasDocumentDownloadTarget(item)
    ? `doc-${item.documentId}`
    : hasFormDownloadTarget(item)
      ? `form-${item.formTemplateId}-${item.entityDataId}`
      : undefined;

  return (
    <PdfModal
      title={item.name}
      url={downloadUrl}
      cacheKey={cacheKey}
      idempotentDownload={!hasDocumentDownloadTarget(item)}
      headers={
        accessToken
          ? { [api.environment.accessTokenKey]: `Bearer ${accessToken}` }
          : undefined
      }
      onRequestClose={onRequestClose}
    />
  );
};

export const DownloadableFormsCardPlugin: ScreenComponentPlugin<DownloadableFormsCardProps> =
  {
    type: 'DownloadableFormsCard',
    name: 'Downloadable Forms Card',
    categories: ['study-progress'],
    component: DownloadableFormsCard
  };
